import { useEffect, useState, useRef } from 'react';

import { UnfoldMore, KeyboardArrowUp, KeyboardArrowDown, ExpandLess, ExpandMore } from '@mui/icons-material';

import { useRolePermissions, useScrollSync, useSnackbar } from '../../hooks';
import { useTheme, useMediaQuery } from '@mui/material';

import { BRAND_STATUS_OPTIONS, BrandFormData, Brand } from '../../types/brand.types';

import { LoadingTable } from '../../components';
import { BrandsForm, BrandsSearch, BrandsActions, BrandsTable } from './components';

import './BrandsPage.css';

import {
  useGetBrandsListQuery,
  useGetBrandsStatusOptionsQuery,
  useUpdateBrandsMutation,
  useCreateBrandMutation,
  useExportBrandsToExcelMutation
} from '../../services';

import {
  columnNames,
  getChangedRows,
  getFilteredAndSortedBrands,
  getPlaceholdersValues,
  getTransformedCreateData,
  getUpdatedBrands
} from './brands-utils';

function BrandsPage() {
    const { setSnackbarMessage } = useSnackbar();
    const { canCreate, canExport } = useRolePermissions();
    const { headerRef: brandsHeaderRef, bodyRef: brandsBodyRef } = useScrollSync();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { data, isSuccess, isLoading, isError } = useGetBrandsStatusOptionsQuery();
  const {
    data: brandsList,
    isSuccess: isBrandsSucess,
    isLoading: isBrandsLoading,
    isError: isBrandsError
  } = useGetBrandsListQuery();

  const [updateBrands] = useUpdateBrandsMutation();
  const [createBrand] = useCreateBrandMutation();
  const [exportBrandsToExcel] = useExportBrandsToExcelMutation();

  const [brands, setBrands] = useState<Brand[]>([]);
  const [changedRows, setChangedRows] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [isExpanded, setIsExpanded] = useState(true);
  const isCollapsible = isMobile;

  const originalBrandsRef = useRef<Brand[]>([]);
  const placeholders = getPlaceholdersValues(brandsList || []);
  const statusOptions = data?.length ? data : BRAND_STATUS_OPTIONS;
  const filteredAndSortedBrands = getFilteredAndSortedBrands(brands, searchTerm, sortBy, sortOrder);

  const isDataInitialized = isBrandsSucess && isSuccess && !isBrandsLoading && !isLoading;
  const isDataError = isBrandsError || isError;

  useEffect(() => {
    if (isDataError) {
      setSnackbarMessage('Помилка завантаження даних', 'error');
    }
  }, [isDataError, setSnackbarMessage]);

  useEffect(() => {
    if (brandsList && Array.isArray(brandsList)) {
      setBrands(brandsList);
      originalBrandsRef.current = brandsList.map(b => ({ ...b }));
      setChangedRows([]);
    }
  }, [brandsList]);

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <UnfoldMore />;
    return sortOrder === 'ASC' ? <KeyboardArrowUp /> : <KeyboardArrowDown />;
  };

  const handleCellValueChange = (id: number, field: string, value: any) => {
    const updated = getUpdatedBrands(brands, id, field, value);
    setBrands(updated);
    const changeRows = getChangedRows(updated, originalBrandsRef);
    setChangedRows(changeRows);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const handleFormSubmit = async (data: BrandFormData) => {
    const newBrand = getTransformedCreateData(data);

    try {
      await createBrand(newBrand).unwrap();
      setSnackbarMessage('Бренд успішно створено', 'success');
    } catch (error: any) {
      const errorMessage = error.data?.error || 'Помилка створення бренду';
      setSnackbarMessage(errorMessage, 'error');
      throw error;
    }
  };

  const handleSaveBrandChanges = async () => {
    if (changedRows.length === 0) return;
    const changedBrands = brands.filter(brand => changedRows.includes(Number(brand.id)));
    try {
      await updateBrands(changedBrands).unwrap();
      setSnackbarMessage('Зміни успішно збережено', 'success');
      setChangedRows([]);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Помилка збереження змін';
      setSnackbarMessage(errorMessage, 'error');
    }
  };

  const handleExportToExcel = async () => {
    try {
      await exportBrandsToExcel().unwrap();
      setSnackbarMessage('Файл успішно вивантажено', 'success');
    } catch (error: any) {
      setSnackbarMessage('Помилка вивантаження файлу', 'error');
    }
  };

  return (
    <div className="brands-main-container">
      <div className="brands-sticky-header">
        <div className={`brands-collapsible${isCollapsible ? (isExpanded ? ' expanded' : ' collapsed') : ''}`}>
          <div className="brands-form-section">
            <div className="brands-form-container">
              {canCreate && (
                <BrandsForm
                  nextSuggestedId={placeholders.id}
                  nextSuggestedOrder={placeholders.order}
                  existingBrandIds={brands.map(b => b.id)}
                  onSubmit={handleFormSubmit}
                />
              )}

              <BrandsSearch searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>

            <BrandsActions
              canCreate={canCreate}
              canExport={canExport}
              hasChanges={changedRows.length > 0}
              onSave={handleSaveBrandChanges}
              onExport={handleExportToExcel}
            />
          </div>
        </div>

        {isCollapsible && (
          <button className="brands-toggle-bar" onClick={() => setIsExpanded(v => !v)}>
            <span className="brands-toggle-summary">
              {!isExpanded && searchTerm
                ? `Пошук: ${searchTerm}`
                : isExpanded
                  ? 'Згорнути фільтри'
                  : 'Розгорнути фільтри'}
            </span>
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </button>
        )}

        <div className="brands-table-header-wrapper" ref={brandsHeaderRef}>
          <div className="brands-table-header">
            {columnNames.map(col => (
              <div key={col.key} className={`brands-header-cell ${col.className}`} onClick={() => handleSort(col.key)}>
                {col.label}
                <div className="brands-sort-icon">{getSortIcon(col.key)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="brands-results-container">
        {!isDataInitialized ? (
          <LoadingTable message="Завантаження брендів..." rows={6} columns={6} />
        ) : (
          <BrandsTable
            bodyRef={brandsBodyRef}
            brands={filteredAndSortedBrands}
            changedRows={changedRows}
            statusOptions={statusOptions}
            canCreate={canCreate}
            onCellValueChange={handleCellValueChange}
          />
        )}
      </div>
    </div>
  );
}

export default BrandsPage;
